import React from "react";
import { motion } from "motion/react";
import { Car, Shield, Clock, Phone, CheckCircle, Star } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { motorista, WHATSAPP_LINK } from "../data/mock";

const MotoristaSection = () => {
  const getWhatsAppUrl = (serviceName) => {
    const message = `Olá! Tenho interesse no serviço ${serviceName}. Gostaria de mais informações.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="motorista" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Transfer & Conforto
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Motorista Particular
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Conforto, segurança e praticidade para suas viagens no Rio de Janeiro.
            Ideal para turistas que querem aproveitar sem preocupações.
          </p>
        </motion.div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-6 mb-14"
        >
          {["Conforto", "Pontualidade", "Segurança", "Ideal para Turistas"].map((highlight, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-[hsl(var(--sand))] px-5 py-3 rounded-full"
            >
              <CheckCircle className="w-5 h-5 text-[hsl(var(--whatsapp))]" />
              <span className="font-medium text-gray-700">{highlight}</span>
            </div>
          ))}
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {motorista.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="group h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                      <Car className="w-3 h-3" />
                      Transfer
                    </span>
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
                    {service.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {service.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    {service.includes.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-[hsl(var(--whatsapp))]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {service.highlights.map((hl, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 bg-[hsl(var(--golden))/0.15] text-[hsl(var(--warm-orange))] text-xs font-medium px-3 py-1 rounded-full"
                      >
                        <Star className="w-3 h-3" />
                        {hl}
                      </span>
                    ))}
                  </div>
                  <motion.a
                    href={getWhatsAppUrl(service.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-3 h-auto font-medium gap-2">
                      <Phone className="w-4 h-4" />
                      Solicitar Motorista
                    </Button>
                  </motion.a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MotoristaSection;
